import { Caption } from 'stories/elements/Typography/Text'
import styled from 'styled-components'
import HomeIcon from 'assets/app/sidebar/home.svg'
import SearchIcon from 'assets/app/sidebar/search.svg'
import CreateContentIcon from 'assets/app/sidebar/add_content.svg'
import DownloadIcon from 'assets/app/sidebar/download.svg'
import QuestionIcon from 'assets/app/sidebar/question.svg'
import theme from 'stories/utils/theme'
import { Know } from 'interfaces/Know'
import { useToggleState } from 'lib/hooks/useToggleState'
import dynamic from 'next/dynamic'
import _useTranslation from 'lib/hooks/_useTranslation'
import { appDeepLinkKnow } from 'lib/features/app-deep-links'
import {
    AnalyticsEventKnowConversionDialogActionButtonClick,
    AnalyticsEventKnowMobileDummyMenuClick,
    AnalyticsEventKnowMobileDummyMenuImpression,
} from 'lib/constants/AnalyticsEventType'
import { trackEvent } from 'lib/tracking/analytics-service'
import { redirectToAppDownloadUrlAsync } from 'lib/features/app-links'
import { getMobileOS } from 'lib/tracking/attribution/device'
import { useRouter } from 'next/router'
import { searchResultRouteOptions } from 'lib/constants/routes'
import { useEffect } from 'react'

const WebappConversionDialog = dynamic(() => import('../../webapp/WebappConversionDialog'))

const Container = styled.div`
    height: 60px;
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    justify-content: center;
    align-items: center;
    border-top: 1px solid ${`${theme.colors.placeholderText}66`};

    .icon {
        display: flex;
        flex-direction: column;
        gap: 5px;
        cursor: pointer;
        justify-content: center;
        align-items: center;
    }
`

interface Props {
    know: Know
    referrerScreen?: string
}

const MobileDummyMenu = ({ know, referrerScreen }: Props) => {
    const [showConversionDialog, toggleConversionDialog] = useToggleState(false)
    const { t: tCommon } = _useTranslation('common')
    const mobileOS = getMobileOS() ?? 'ios'
    const router = useRouter()

    useEffect(() => {
        trackEvent(AnalyticsEventKnowMobileDummyMenuImpression, {
            know: know ?? undefined,
            referrerScreen,
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    enum MenuOptions {
        Home = 'Home',
        Download = 'Download',
        Upload = 'Upload',
        Search = 'Search',
        Questions = 'Questions',
    }

    const menuOptions = [
        { text: tCommon('common/navBarHomeLink'), icon: <HomeIcon />, value: MenuOptions.Home },
        { text: tCommon('common/footerDownloadLink'), icon: <DownloadIcon />, value: MenuOptions.Download },
        { text: tCommon('common/upload'), icon: <CreateContentIcon />, value: MenuOptions.Upload },
        { text: tCommon('common/searchButtonLabel'), icon: <SearchIcon />, value: MenuOptions.Search },
        { text: tCommon('common/navBarMenuQuestionMenuItem'), icon: <QuestionIcon />, value: MenuOptions.Questions },
    ]

    const returnRedirectSubjectRoute = () => router.push(searchResultRouteOptions('', undefined, { referrerScreen }))

    const handleDummyBannerButtonClick = (value: MenuOptions) => {
        trackEvent(AnalyticsEventKnowMobileDummyMenuClick, {
            know: know ?? undefined,
            referrerScreen,
            value: value,
        })

        switch (value) {
            case MenuOptions.Download:
                redirectToAppDownloadUrlAsync(mobileOS, 'mobileDummyBanner', appDeepLinkKnow(know.uuid))
                break
            case MenuOptions.Search:
                returnRedirectSubjectRoute()
                break
            case MenuOptions.Home:
            case MenuOptions.Upload:
            case MenuOptions.Questions:
            default:
                toggleConversionDialog()
                break
        }
    }

    const handleConversionDialogActionButtonClick = (platform: 'android' | 'ios') => {
        trackEvent(AnalyticsEventKnowConversionDialogActionButtonClick, {
            know: know ?? undefined,
            referrerScreen,
        })
        redirectToAppDownloadUrlAsync(platform, 'mobileDummyBanner', appDeepLinkKnow(know.uuid))
    }

    return (
        <>
            <Container>
                {menuOptions.map((option) => (
                    <div key={option.value} className="icon" onClick={() => handleDummyBannerButtonClick(option.value)}>
                        {option.icon}
                        <Caption fontFamily="var(--font-inter)" textAlign="center" fontWeight={500} key={option.text}>
                            {option.text}
                        </Caption>
                    </div>
                ))}
            </Container>
            {know && showConversionDialog ? (
                <WebappConversionDialog
                    open={showConversionDialog}
                    title={tCommon('common/downloadAppToUseThisFeatureTitle')}
                    handleClose={toggleConversionDialog}
                    onButtonClick={handleConversionDialogActionButtonClick}
                    downloadDeepLinkPath={appDeepLinkKnow(know.uuid)}
                    know={know}
                    referrerScreen={referrerScreen}
                />
            ) : null}
        </>
    )
}

export default MobileDummyMenu
